/**
 * ConversationWidget
 *
 * Pattern: DetailPanel (pannello laterale destro, mobile = full screen)
 * Unified with all other widgets in the platform for consistent 50% width layout.
 *
 * IMPORTANTE: Questo widget è INTELLIGENTE:
 * - Se conversationId esiste → mostra VIEW NATIVO in base al canale (email/chat/call)
 * - Se conversationId non esiste → mostra form di creazione
 *
 * Questo segue il principio: "I widget dovrebbero riflettere il modello nativo"
 */

import { useState, useRef } from 'react';
import {
  Search,
  Mail,
  MessageCircle,
  Send,
  Phone,
  Video,
  MessageSquare,
  Briefcase,
  X,
} from 'lucide-react';
import { useHubStore, useProjectsStore } from '@business-assistant/shared';
import type { Channel } from '@business-assistant/shared/domain';
import { DetailPanel, DetailSection, DetailActions } from '../common/base';
import { EmailWidget } from './EmailWidget';
import { MessageWidget } from './MessageWidget';
import { CallWidget } from './CallWidget';
import { useTranslation } from '../../hooks/useTranslation';

// =============================================================================
// TYPES
// =============================================================================

export interface ConversationFormData {
  channel: Channel;
  title: string;
  contactId?: string;
  contactName?: string;
  projectId?: string;
  initialMessage?: string;
}

interface ConversationWidgetProps {
  conversationId?: string; // edit mode
  preselectedContactId?: string; // per aprire da contatto
  preselectedChannel?: Channel;
  onClose: () => void;
  onSave: (data: ConversationFormData) => void;
}

// =============================================================================
// CHANNEL CONFIG
// =============================================================================

interface ChannelConfig {
  labelKey: string;
  icon: React.ComponentType<{ size?: number; style?: React.CSSProperties }>;
  color: string;
  bgColor: string;
}

const CHANNEL_CONFIG: Record<Channel, ChannelConfig> = {
  email: {
    labelKey: 'conversations.channels.email',
    icon: Mail,
    color: '#3b82f6',
    bgColor: '#dbeafe',
  },
  whatsapp: {
    labelKey: 'conversations.channels.whatsapp',
    icon: MessageCircle,
    color: '#22c55e',
    bgColor: '#dcfce7',
  },
  telegram: {
    labelKey: 'conversations.channels.telegram',
    icon: Send,
    color: '#0ea5e9',
    bgColor: '#e0f2fe',
  },
  call: {
    labelKey: 'conversations.channels.call',
    icon: Phone,
    color: '#8b5cf6',
    bgColor: '#ede9fe',
  },
  videocall: {
    labelKey: 'conversations.channels.videocall',
    icon: Video,
    color: '#ec4899',
    bgColor: '#fce7f3',
  },
  sms: {
    labelKey: 'conversations.channels.sms',
    icon: MessageSquare,
    color: '#f59e0b',
    bgColor: '#fef3c7',
  },
};

const CHANNEL_OPTIONS: Channel[] = ['email', 'whatsapp', 'telegram', 'call', 'videocall', 'sms'];

// =============================================================================
// COMPONENT
// =============================================================================

export function ConversationWidget({
  conversationId,
  preselectedContactId,
  preselectedChannel,
  onClose,
  onSave,
}: ConversationWidgetProps) {
  const { t } = useTranslation();
  const { contacts, conversations, getContact, getConversationMessages, addMessage } = useHubStore();
  const { projects } = useProjectsStore();

  // Get existing conversation if editing/viewing
  const existingConversation = conversationId
    ? conversations.find((c) => c.id === conversationId)
    : null;

  // =========================================================================
  // VIEW MODE: Se abbiamo una conversazione esistente, mostra vista nativa
  // =========================================================================
  if (existingConversation) {
    const messages = getConversationMessages(conversationId!);
    const contact = existingConversation.contactId
      ? getContact(existingConversation.contactId)
      : null;

    // EMAIL → Mostra EmailWidget con vista Outlook-style
    if (existingConversation.channel === 'email') {
      const lastMessage = messages[messages.length - 1];
      const emailData = {
        id: existingConversation.id,
        from: lastMessage?.direction === 'in'
          ? (contact?.handles.email || contact?.displayName || 'sconosciuto@email.com')
          : 'io@azienda.com',
        fromName: lastMessage?.direction === 'in' ? contact?.displayName : 'Tu',
        to: lastMessage?.direction === 'out'
          ? (contact?.handles.email || contact?.displayName || 'destinatario@email.com')
          : 'io@azienda.com',
        toName: lastMessage?.direction === 'out' ? contact?.displayName : 'Tu',
        subject: existingConversation.title,
        body: messages.map(m => m.text).join('\n\n---\n\n'),
        direction: (lastMessage?.direction || 'in') as 'in' | 'out',
        at: existingConversation.lastAt,
        read: !existingConversation.unread,
        attachments: [],
      };

      return (
        <EmailWidget
          email={emailData}
          onClose={onClose}
          onSend={(data) => {
            addMessage(conversationId!, {
              text: data.body,
              direction: 'out',
            });
            onSave({
              channel: 'email',
              title: existingConversation.title,
              contactId: existingConversation.contactId,
              contactName: existingConversation.contactName,
              projectId: existingConversation.projectId,
            });
          }}
        />
      );
    }

    // WHATSAPP/TELEGRAM/SMS → Mostra MessageWidget con chat bubbles
    if (['whatsapp', 'telegram', 'sms'].includes(existingConversation.channel)) {
      const conversationData = {
        id: existingConversation.id,
        platform: existingConversation.channel === 'whatsapp' ? 'whatsapp' as const
          : existingConversation.channel === 'telegram' ? 'telegram' as const
          : 'sms' as const,
        contactId: contact?.id,
        contactName: contact?.displayName || existingConversation.contactName || 'Sconosciuto',
        contactPhone: contact?.handles.phone || contact?.handles.whatsapp,
        messages: messages.map(m => ({
          id: m.id,
          platform: existingConversation.channel === 'whatsapp' ? 'whatsapp' as const
            : existingConversation.channel === 'telegram' ? 'telegram' as const
            : 'sms' as const,
          text: m.text,
          direction: m.direction as 'in' | 'out',
          at: m.at,
          read: m.read,
        })),
        lastAt: existingConversation.lastAt,
        unreadCount: existingConversation.unread,
      };

      return (
        <MessageWidget
          conversation={conversationData}
          onClose={onClose}
          onSendMessage={(text) => {
            addMessage(conversationId!, {
              text,
              direction: 'out',
            });
          }}
          onCall={() => console.log('Avvio chiamata...')}
        />
      );
    }

    // CALL/VIDEOCALL → Mostra CallWidget con dettagli chiamata
    if (existingConversation.channel === 'call' || existingConversation.channel === 'videocall') {
      const callData = {
        id: existingConversation.id,
        contactId: contact?.id,
        contactName: contact?.displayName || existingConversation.contactName,
        phone: contact?.handles.phone,
        direction: 'in' as const,
        duration: 180, // 3 minutes demo
        at: existingConversation.lastAt,
        answered: true,
      };

      return (
        <CallWidget
          call={callData}
          onClose={onClose}
          onCallback={() => console.log('Richiamando...')}
          onSendMessage={() => console.log('Aprendo chat...')}
        />
      );
    }
  }

  // =========================================================================
  // CREATE MODE: Form per creare nuova conversazione
  // =========================================================================

  // Get preselected contact
  const preselectedContact = preselectedContactId ? getContact(preselectedContactId) : null;

  // Form state (solo per CREATE MODE)
  const [formData, setFormData] = useState<ConversationFormData>({
    channel: preselectedChannel || 'email',
    title: '',
    contactId: preselectedContactId,
    contactName: preselectedContact?.displayName,
    projectId: undefined,
    initialMessage: '',
  });

  // Contact search state
  const [contactSearch, setContactSearch] = useState(
    preselectedContact?.displayName || ''
  );
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const contactInputRef = useRef<HTMLInputElement>(null);

  // Filter contacts based on search
  const filteredContacts = contacts
    .filter(
      (contact) =>
        contact.displayName.toLowerCase().includes(contactSearch.toLowerCase()) ||
        contact.handles.email?.toLowerCase().includes(contactSearch.toLowerCase()) ||
        contact.handles.phone?.includes(contactSearch) ||
        contact.handles.whatsapp?.includes(contactSearch) ||
        contact.handles.telegram?.toLowerCase().includes(contactSearch.toLowerCase())
    )
    .slice(0, 5);

  // Get selected contact for preview
  const selectedContact = formData.contactId ? getContact(formData.contactId) : null;

  // Handle contact selection
  const handleSelectContact = (contact: typeof contacts[0]) => {
    setFormData({
      ...formData,
      contactId: contact.id,
      contactName: contact.displayName,
    });
    setContactSearch(contact.displayName);
    setShowContactDropdown(false);
  };

  // Handle manual contact name input (no contact selected)
  const handleContactInputChange = (value: string) => {
    setContactSearch(value);
    // Clear contact selection if manually typing
    if (formData.contactId && selectedContact?.displayName !== value) {
      setFormData({
        ...formData,
        contactId: undefined,
        contactName: value,
      });
    } else if (!formData.contactId) {
      setFormData({
        ...formData,
        contactName: value,
      });
    }
    setShowContactDropdown(true);
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  // Get channel config
  const currentChannel = CHANNEL_CONFIG[formData.channel];
  const ChannelIcon = currentChannel.icon;

  return (
    <DetailPanel
      title={t('conversations.newConversation')}
      icon={<ChannelIcon size={20} style={{ color: currentChannel.color }} />}
      onClose={onClose}
      accentColor={currentChannel.color}
      width="50%"
    >
      <form onSubmit={handleSubmit}>
        {/* Channel Select */}
        <DetailSection title={t('conversations.channel')}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '0.5rem',
            }}
          >
            {CHANNEL_OPTIONS.map((channel) => {
              const config = CHANNEL_CONFIG[channel];
              const Icon = config.icon;
              const isSelected = formData.channel === channel;
              return (
                <button
                  key={channel}
                  type="button"
                  onClick={() => setFormData({ ...formData, channel })}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.375rem',
                    padding: '0.75rem 0.5rem',
                    border: `2px solid ${isSelected ? config.color : 'var(--line)'}`,
                    borderRadius: '0.5rem',
                    background: isSelected ? config.bgColor : 'white',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <Icon
                    size={20}
                    style={{ color: isSelected ? config.color : 'var(--text-muted)' }}
                  />
                  <span
                    style={{
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      color: isSelected ? config.color : 'var(--text-secondary)',
                    }}
                  >
                    {t(config.labelKey)}
                  </span>
                </button>
              );
            })}
          </div>
        </DetailSection>

        {/* Title */}
        <DetailSection title={t('conversations.titleRequired')}>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
            placeholder={t('conversations.titlePlaceholder')}
            style={{
              width: '100%',
              padding: '0.625rem',
              border: '1px solid var(--line)',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
            }}
          />
        </DetailSection>

        {/* Contact Search with Autocomplete */}
        <DetailSection title={t('conversations.contact')}>
          <div style={{ position: 'relative' }}>
            <div style={{ position: 'relative' }}>
              <Search
                size={16}
                style={{
                  position: 'absolute',
                  left: '0.625rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-muted)',
                  pointerEvents: 'none',
                }}
              />
              <input
                ref={contactInputRef}
                type="text"
                value={contactSearch}
                onChange={(e) => handleContactInputChange(e.target.value)}
                onFocus={() => setShowContactDropdown(true)}
                onBlur={() => setTimeout(() => setShowContactDropdown(false), 200)}
                placeholder={t('conversations.searchContactPlaceholder')}
                style={{
                  width: '100%',
                  padding: '0.625rem',
                  paddingLeft: '2.25rem',
                  border: '1px solid var(--line)',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                }}
              />
            </div>

            {/* Contact dropdown */}
            {showContactDropdown && filteredContacts.length > 0 && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: 'white',
                  border: '1px solid var(--line)',
                  borderRadius: '0.375rem',
                  boxShadow: 'var(--shadow-hover)',
                  zIndex: 10,
                  maxHeight: '200px',
                  overflowY: 'auto',
                  marginTop: '0.25rem',
                }}
              >
                {filteredContacts.map((contact) => (
                  <button
                    key={contact.id}
                    type="button"
                    onClick={() => handleSelectContact(contact)}
                    style={{
                      width: '100%',
                      padding: '0.625rem',
                      background: 'transparent',
                      border: 'none',
                      borderBottom: '1px solid var(--line)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <div
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        background: currentChannel.color,
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        flexShrink: 0,
                      }}
                    >
                      {contact.displayName[0]?.toUpperCase() || '?'}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 500, fontSize: '0.875rem' }}>
                        {contact.displayName}
                      </div>
                      <div
                        style={{
                          fontSize: '0.75rem',
                          color: 'var(--text-muted)',
                          display: 'flex',
                          gap: '0.5rem',
                        }}
                      >
                        {contact.company && <span>{contact.company}</span>}
                        {contact.handles.email && <span>{contact.handles.email}</span>}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </DetailSection>

        {/* Selected Contact Preview */}
        {selectedContact && (
          <DetailSection title={t('conversations.selectedContact')}>
            <div
              style={{
                padding: '0.75rem',
                background: currentChannel.bgColor,
                borderRadius: '0.5rem',
                border: `1px solid ${currentChannel.color}20`,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                }}
              >
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: currentChannel.color,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1rem',
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  {selectedContact.displayName[0]?.toUpperCase() || '?'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.9375rem' }}>
                    {selectedContact.displayName}
                  </div>
                  <div
                    style={{
                      fontSize: '0.75rem',
                      color: 'var(--text-muted)',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '0.5rem',
                    }}
                  >
                    {selectedContact.company && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <Briefcase size={12} />
                        {selectedContact.company}
                      </span>
                    )}
                    {selectedContact.role && <span>{selectedContact.role}</span>}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setFormData({ ...formData, contactId: undefined, contactName: undefined });
                    setContactSearch('');
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: '0.25rem',
                    cursor: 'pointer',
                    color: 'var(--text-muted)',
                  }}
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          </DetailSection>
        )}

        {/* Project Select (Optional) */}
        <DetailSection title={t('conversations.linkToProject')}>
          <select
            value={formData.projectId || ''}
            onChange={(e) =>
              setFormData({
                ...formData,
                projectId: e.target.value || undefined,
              })
            }
            style={{
              width: '100%',
              padding: '0.625rem',
              border: '1px solid var(--line)',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              background: 'white',
            }}
          >
            <option value="">{t('conversations.noProject')}</option>
            {projects
              .filter((p) => p.status !== 'chiuso')
              .map((project) => (
                <option key={project.id} value={project.id}>
                  {project.title}
                </option>
              ))}
          </select>
        </DetailSection>

        {/* Initial Message */}
        <DetailSection title={t('conversations.initialMessage')}>
          <textarea
            value={formData.initialMessage || ''}
            onChange={(e) => setFormData({ ...formData, initialMessage: e.target.value })}
            placeholder={t('conversations.initialMessagePlaceholder')}
            rows={4}
            style={{
              width: '100%',
              padding: '0.625rem',
              border: '1px solid var(--line)',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              resize: 'vertical',
              fontFamily: 'inherit',
            }}
          />
        </DetailSection>

        {/* Actions */}
        <DetailActions>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '0.625rem 1.25rem',
              border: '1px solid var(--line)',
              borderRadius: '0.375rem',
              background: 'white',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '0.875rem',
            }}
          >
            {t('conversations.cancel')}
          </button>
          <button
            type="submit"
            style={{
              padding: '0.625rem 1.25rem',
              border: 'none',
              borderRadius: '0.375rem',
              background: currentChannel.color,
              color: 'white',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '0.875rem',
            }}
          >
            {t('conversations.createConversation')}
          </button>
        </DetailActions>
      </form>
    </DetailPanel>
  );
}
